import React from 'react';
import SortableHeader from './SortableHeader';

interface NestedTableHeaderProps {
  localRows: any[];
  selectedRows: string[];
  handleSelectAll: () => void;
  handleDeleteSelected: () => void;
  columnOrder: string[];
  hiddenColumns: string[];
  columnConfig: any;
  columnWidths: { [key: string]: number };
  resizing: string | null;
  draggedColumn: string | null;
  dragOverColumn: string | null;
  sortField: string | null;
  sortDirection: 'asc' | 'desc';
  handleDragStart: (e: React.DragEvent, column: string) => void;
  handleDragOver: (e: React.DragEvent, column: string) => void;
  handleDragLeave: () => void;
  handleDrop: (e: React.DragEvent, targetColumn: string) => void;
  handleDragEnd: () => void;
  handleSort: (field: string) => void;
  handleResizeStart: (e: React.MouseEvent, column: string) => void;
}

const NestedTableHeader: React.FC<NestedTableHeaderProps> = ({
  localRows,
  selectedRows,
  handleSelectAll,
  handleDeleteSelected,
  columnOrder,
  hiddenColumns,
  columnConfig,
  columnWidths,
  resizing,
  draggedColumn,
  dragOverColumn,
  sortField,
  sortDirection,
  handleDragStart,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleDragEnd,
  handleSort,
  handleResizeStart
}) => (
  <thead className="sticky top-0 z-[5]" style={{ backgroundColor: 'var(--color-background)' }}>
    <tr>
      {/* Column 1: Master checkbox - selects all rows */}
      <th className="border border-gray-300 px-1 py-2 text-center font-semibold text-gray-700" style={{width: '40px', minWidth: '40px', maxWidth: '40px', backgroundColor: 'var(--color-background)'}}>
        <input
          type="checkbox"
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          checked={localRows.filter(row => row._id).length > 0 && selectedRows.length === localRows.filter(row => row._id).length}
          ref={(el) => {
            if (el) el.indeterminate = selectedRows.length > 0 && selectedRows.length < localRows.filter(row => row._id).length;
          }}
          onChange={handleSelectAll}
          title="Select all"
        />
      </th>
      {/* Column 2: Trash icon - deletes selected rows */}
      <th className="border border-gray-300 px-1 py-2 text-center font-semibold text-gray-700" style={{width: '40px', minWidth: '40px', maxWidth: '40px', backgroundColor: 'var(--color-background)'}}>
        <button
          onClick={handleDeleteSelected}
          disabled={selectedRows.length === 0}
          className={`p-1 rounded ${selectedRows.length > 0 ? 'text-red-600 hover:bg-red-50 cursor-pointer' : 'text-gray-300 cursor-not-allowed'} transition-colors`}
          title={selectedRows.length > 0 ? `Delete ${selectedRows.length} selected items` : 'No items selected'}
        >
          <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </th>
      {columnOrder.filter(field => !hiddenColumns.includes(field)).map((field) => (
        <SortableHeader
          key={field + '-' + columnOrder.join('-')}
          field={field}
          style={{
            width: columnWidths[field] ? `${columnWidths[field]}px` : columnConfig[field as keyof typeof columnConfig].width
          }}
          resizing={resizing}
          draggedColumn={draggedColumn}
          dragOverColumn={dragOverColumn}
          sortField={sortField}
          sortDirection={sortDirection}
          handleDragStart={handleDragStart}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleDrop={handleDrop}
          handleDragEnd={handleDragEnd}
          handleSort={handleSort}
          handleResizeStart={handleResizeStart}
          columnWidths={columnWidths}
        >
          {columnConfig[field as keyof typeof columnConfig].label}
        </SortableHeader>
      ))}
    </tr>
  </thead>
);

export default NestedTableHeader;